import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import { patientsService } from '../../services/patients/patientsService';
import { getChatStatusWebSocketUrl } from '../../../../shared/utils/constants';
import './PatientDetailPage.css';

const USE_CHAT_WEBSOCKET = process.env.REACT_APP_USE_CHAT_WEBSOCKET === 'true' || false;

/**
 * Dedicated page that shows only the requirements chat conversation log.
 * Used when "View conversation" is clicked from the diet plan sidebar (no diet plan hierarchy).
 */
const RequirementsConversationPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId, goalId } = useParams<{ patientId: string; goalId: string }>();
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatMessageInput, setChatMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatWsConnected, setChatWsConnected] = useState(false);
  const [workflowState, setWorkflowState] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>({});

  const lastSeenMessageIdRef = useRef<number>(0);
  const chatPollingRef = useRef<{ abortController: AbortController | null }>({ abortController: null });
  const chatWsRef = useRef<WebSocket | null>(null);
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

  const updateChatMessages = useCallback((messages: any[]) => {
    setChatMessages(Array.isArray(messages) ? messages : []);
  }, []);

  // Load meal plan to get requirementsChatSessionId and patient name
  useEffect(() => {
    if (!patientId || !goalId) {
      setError('Missing patient or diet plan');
      setLoading(false);
      return;
    }
    const numericPatientId = Number(patientId);
    const numericGoalId = Number(goalId);
    if (Number.isNaN(numericPatientId) || Number.isNaN(numericGoalId)) {
      setError('Invalid patient or diet plan');
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      patientsService.getPatientMealPlanByGoalId(numericPatientId, numericGoalId),
      patientsService.getPatientDetails(numericPatientId),
    ])
      .then(([plan, patient]) => {
        if (cancelled) return;
        if (!plan?.requirementsChatSessionId) {
          setError('This diet plan has no requirements conversation.');
          setLoading(false);
          return;
        }
        setSessionId(plan.requirementsChatSessionId);
        setPatientName(patient?.name ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err?.message || 'Failed to load diet plan');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, goalId]);

  // Long-polling for chat messages
  const startChatPolling = useCallback((sid: number) => {
    if (chatPollingRef.current.abortController) {
      chatPollingRef.current.abortController.abort();
    }
    const abortController = new AbortController();
    chatPollingRef.current.abortController = abortController;

    const poll = async () => {
      if (abortController.signal.aborted || document.visibilityState === 'hidden') return;
      try {
        const syncResponse = await patientsService.syncMessages(sid, lastSeenMessageIdRef.current, 25);
        if (abortController.signal.aborted) return;
        if (syncResponse.messages?.length > 0) {
          setChatMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const newMessages = syncResponse.messages.filter((m: any) => !existingIds.has(m.id));
            return [...prev, ...newMessages].sort((a, b) => a.id - b.id);
          });
          const maxId = Math.max(...syncResponse.messages.map((m: any) => m.id));
          lastSeenMessageIdRef.current = Math.max(lastSeenMessageIdRef.current, maxId);
          setSendingMessage(false);
        } else {
          lastSeenMessageIdRef.current = syncResponse.next_since;
        }
        const hadNewData = (syncResponse.messages?.length > 0) || (syncResponse.status_updates?.length > 0);
        const minDelayMs = hadNewData ? 0 : 2000;
        if (!abortController.signal.aborted) {
          if (minDelayMs > 0) await new Promise((r) => setTimeout(r, minDelayMs));
          if (!abortController.signal.aborted) poll();
        }
      } catch (err: any) {
        if (abortController.signal.aborted) return;
        if (err?.response?.status !== 503 && err?.response?.status !== 0) {
          console.error('Failed to sync messages:', err);
        }
        await new Promise((r) => setTimeout(r, 1000));
        if (!abortController.signal.aborted) poll();
      }
    };
    poll();
    return () => {
      abortController.abort();
      chatPollingRef.current.abortController = null;
    };
  }, []);

  const buildChatWsUrl = useCallback((sid: number) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    return getChatStatusWebSocketUrl(sid, apiBase);
  }, []);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!USE_CHAT_WEBSOCKET || !sessionId) {
      if (chatWsRef.current) {
        try { chatWsRef.current.close(); } catch { /* ignore */ }
        chatWsRef.current = null;
      }
      setChatWsConnected(false);
      return;
    }
    const sid = sessionId;
    const { url: wsUrl, sendFirstMessage } = buildChatWsUrl(sid);
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(wsUrl);
      chatWsRef.current = ws;
    } catch (e) {
      setChatWsConnected(false);
      return;
    }
    ws.onopen = () => {
      setChatWsConnected(true);
      if (sendFirstMessage && ws) {
        try { ws.send(JSON.stringify({ session_id: sid })); } catch { /* ignore */ }
      }
    };
    ws.onmessage = (event) => {
      try {
        const statusMessage = JSON.parse(event.data);
        const status = statusMessage?.status;
        let eventData: any = null;
        if (statusMessage?.message) {
          try {
            eventData = typeof statusMessage.message === 'string' ? JSON.parse(statusMessage.message) : statusMessage.message;
          } catch { eventData = null; }
        }
        if (status === 'message_added' || status === 'new_message' || eventData?.message_type === 'message_added') {
          setSendingMessage(false);
          void patientsService.getChatMessages(sid).then(updateChatMessages).catch(console.error);
        }
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };
    ws.onerror = () => setChatWsConnected(false);
    ws.onclose = () => {
      setChatWsConnected(false);
      if (chatWsRef.current === ws) chatWsRef.current = null;
    };
    return () => {
      try { ws?.close(); } catch { /* ignore */ }
      if (chatWsRef.current === ws) chatWsRef.current = null;
      setChatWsConnected(false);
    };
  }, [sessionId, buildChatWsUrl, updateChatMessages]);

  useEffect(() => {
    if (!sessionId) return;
    const cleanup = startChatPolling(sessionId);
    return cleanup;
  }, [sessionId, startChatPolling]);

  // Load initial messages
  useEffect(() => {
    if (!sessionId) return;
    patientsService.getChatMessages(sessionId).then(updateChatMessages).catch(console.error);
  }, [sessionId, updateChatMessages]);

  // Load workflow state (for pending question)
  useEffect(() => {
    if (!sessionId) return;
    patientsService.getRequirementsChatState(sessionId).then((state) => {
      if (state?.success && state?.has_state) setWorkflowState(state);
    }).catch(() => {});
  }, [sessionId]);

  const handleSendChatMessage = async (overrideMessage?: string) => {
    const text = (overrideMessage != null ? overrideMessage : chatMessageInput).trim();
    if (!sessionId || !text || sendingMessage) return;
    try {
      setChatMessageInput('');
      setSelectedOptions({});
      setSendingMessage(true);
      await patientsService.sendRequirementsChatMessage(sessionId, text);
      const messages = await patientsService.getChatMessages(sessionId);
      updateChatMessages(messages);
    } catch (err: any) {
      console.error('Send message failed:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (!sessionId) return;
    const container = chatMessagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [sessionId, chatMessages.length]);

  const handleClose = () => {
    navigate(patientId && goalId ? `/patients/${patientId}/diet-plan/${goalId}/daily-nutrition` : '/patients');
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => navigate('/profile');

  const currentPath = window.location.pathname;
  const sidebarContext =
    patientId && goalId
      ? {
          patientId,
          patientName: patientName ?? undefined,
          goalId,
          requirementsChatSessionId: sessionId ?? undefined,
        }
      : undefined;
  const sidebarItems = createSidebarItems(navigate, currentPath, sidebarContext);

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading conversation...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sessionId) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="section-card">
                <div className="error-message">{error ?? 'No conversation found'}</div>
                <button type="button" className="secondary-button" onClick={handleClose} style={{ marginTop: 16 }}>
                  Back to diet plan
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="section-card" style={{ maxWidth: 900, margin: '0 auto' }}>
              <div className="requirements-chat-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <h3 style={{ margin: 0 }}>Review Nutrition Requirements</h3>
                  <button type="button" className="secondary-button" onClick={handleClose}>
                    Close
                  </button>
                </div>
                <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: 14 }}>
                  Please review the calculated requirements and approve or request modifications.
                </p>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  Session ID: {sessionId} | Messages: {chatMessages.length} | WS: {chatWsConnected ? 'Connected' : 'Disconnected'}
                </p>
              </div>
              <div
                ref={chatMessagesContainerRef}
                className="requirements-chat-messages"
                style={{
                  maxHeight: 420,
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: 16,
                  backgroundColor: '#f9fafb',
                  marginBottom: 16,
                }}
              >
                {chatMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Loading messages...</div>
                ) : (
                  chatMessages.map((message) => {
                    const isDoctor = message.role === 'user' || message.sender_type === 'doctor';
                    const optionsMatch = message.content?.match?.(/\*\*Recommended options:\*\*\s*([^\n]+)/);
                    const optionsText = optionsMatch ? optionsMatch[1].trim() : null;
                    const options: string[] = optionsText ? optionsText.split(',').map((o: string) => o.trim()).filter(Boolean) : [];
                    const questionText = optionsMatch ? message.content.substring(0, optionsMatch.index).trim() : message.content;
                    return (
                      <div
                        key={message.id}
                        style={{
                          marginBottom: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isDoctor ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            padding: '10px 14px',
                            borderRadius: 12,
                            backgroundColor: isDoctor ? '#6366f1' : '#f3f4f6',
                            color: isDoctor ? '#ffffff' : '#111827',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          <div style={{ fontSize: 14, lineHeight: 1.4 }}>{questionText}</div>
                          {!isDoctor && options.length > 0 && (
                            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {options.map((option: string, idx: number) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={async () => {
                                    setSelectedOptions((prev) => ({ ...prev, [message.id]: option }));
                                    setChatMessageInput(option);
                                    await handleSendChatMessage();
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: selectedOptions[message.id] === option ? '#6366f1' : '#ffffff',
                                    color: selectedOptions[message.id] === option ? '#ffffff' : '#111827',
                                    border: `1px solid ${selectedOptions[message.id] === option ? '#6366f1' : '#d1d5db'}`,
                                    borderRadius: 6,
                                    fontSize: 13,
                                    cursor: sendingMessage ? 'not-allowed' : 'pointer',
                                    opacity: sendingMessage ? 0.6 : 1,
                                  }}
                                  disabled={sendingMessage}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                          {message.created_at && (
                            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                              {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {workflowState?.waitingForDoctorResponse && (workflowState.pendingGoalQuestion || workflowState.pendingGoalField) && (
                  <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '10px 14px',
                        borderRadius: 12,
                        backgroundColor: '#f3f4f6',
                        color: '#111827',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                        {workflowState.pendingGoalQuestion || `Answer: ${workflowState.pendingGoalField}`}
                      </div>
                      {Array.isArray(workflowState.pendingGoalOptions) && workflowState.pendingGoalOptions.length > 0 && (
                        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {workflowState.pendingGoalOptions.map((opt: string | number, idx: number) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSendChatMessage(String(opt))}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#ffffff',
                                color: '#111827',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 13,
                                cursor: sendingMessage ? 'not-allowed' : 'pointer',
                                opacity: sendingMessage ? 0.6 : 1,
                              }}
                              disabled={sendingMessage}
                            >
                              {String(opt)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="requirements-chat-input" style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={chatMessageInput}
                  onChange={(e) => setChatMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && chatMessageInput.trim() && !sendingMessage) handleSendChatMessage();
                  }}
                  placeholder="Type 'approve' or describe modifications..."
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                  disabled={sendingMessage}
                />
                <button
                  onClick={() => handleSendChatMessage()}
                  disabled={!chatMessageInput.trim() || sendingMessage}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6366f1',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: sendingMessage ? 'not-allowed' : 'pointer',
                    opacity: !chatMessageInput.trim() || sendingMessage ? 0.5 : 1,
                  }}
                >
                  {sendingMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default RequirementsConversationPage;
